import { Button, Chip, MenuItem, Select } from "@mui/material";
import React, { useCallback, useEffect, useState } from "react";
import ActivateUser from "./ActivateUserModal.js";
import { Link } from "react-router-dom";
import showToast from "../../Helpers/toast";
import ErrorHandler from "../Shared/ErrorHandler.js";
import useApi from "../../Hooks/useApi";
import Modal from "./index.js";
import AssignRole from "./AssignRoleModal.js";
import { Mail, Building, Briefcase, Shield, User } from "lucide-react";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";

const UserActionModal = ({ data = [], onClose, refetch }) => {
  const [isRoleModalOpen, setIsRoleModalOpen] = useState(false);
  const [departments, setDepartments] = useState([]);
  const [designations, setDesignations] = useState([]);

  // local copy of user data (keeps modal in sync + allows optimistic updates)
  const [user, setUser] = useState(data);

  useEffect(() => {
    setUser(data); // sync when parent provides new data
  }, [data]);

  // edit states
  const [editField, setEditField] = useState(null); // "designation" | "department"
  const [selected, setSelected] = useState(null);

  const { error, request } = useApi();
  const { request: publicRequest } = useApi({ isPublic: true });

  const getDepartments = useCallback(async () => {
    try {
      const response = await publicRequest("GET", "/v1/departments");
      setDepartments(response);
    } catch (error) {
      console.log(error);
    }
  }, [publicRequest]);

  const getDesignations = useCallback(async () => {
    try {
      const response = await publicRequest("GET", "/v1/designations");
      setDesignations(response);
    } catch (error) {
      console.log(error);
    }
  }, [publicRequest]);

  useEffect(() => {
    getDepartments();
    getDesignations();
  }, [getDepartments, getDesignations]);

  const handleEdit = (type) => {
    setEditField(type);

    if (type === "designation") {
      const current = designations.find((d) => d.name === user.designation);
      setSelected(current?.id || "");
    } else if (type === "department") {
      const current = departments.find((d) => d.name === user.department);
      setSelected(current?.id || "");
    }
  };

  const handleCancel = () => {
    setEditField(null);
    setSelected(null);
  };

  const handleSave = async () => {
    if (!selected) {
      showToast("Please select a value", "error");
      return;
    }

    const endpoint =
      editField === "designation"
        ? `/v1/user/${user.userId}/designation`
        : `/v1/user/${user.userId}/department`;

    try {
      await request("PUT", endpoint, {
        data: {
          [editField === "designation" ? "designationId" : "departmentId"]:
            selected,
        },
      });

      // 🔥 Optimistically update local state
      setUser((prev) => ({
        ...prev,
        designation:
          editField === "designation"
            ? designations.find((d) => d.id === selected)?.name
            : prev.designation,
        department:
          editField === "department"
            ? departments.find((d) => d.id === selected)?.name
            : prev.department,
      }));

      showToast({
        message: `${editField?.toUpperCase()} updated successfully`,
        type: "success",
      });

      refetch(); // still refresh parent copy
      setEditField(null);
    } catch (err) {
      showToast("Update failed", "error");
      console.error(err);
    }
  };

  if (error) {
    return <ErrorHandler errors={error} />;
  }

  return (
    <div className="modal-content space-y-3">
      {/* header */}
      <div className="flex items-center gap-3">
        <div className="size-12 rounded-full bg-gray-500/10 flex items-center justify-center">
          <User className="size-8 text-gray-700" />
        </div>
        <div className="flex-1">
          <div className="text-xl font-semibold flex items-center gap-3">
            {user?.fullName}
            <Chip
              variant={user.status === "active" ? "default" : "secondary"}
              className="text-xs px-5 py-0.5 rounded-full font-thin"
              label={user?.status === "active" ? "Active" : "Inactive"}
            />
          </div>
        </div>
      </div>

      {/* details */}
      <div className="grid gap-3 text-sm">
        {/* Email */}
        <div className="flex items-center gap-3 p-2 rounded-2xl bg-muted/30">
          <Mail className="size-4 text-muted-foreground" />
          <div>
            <span className="text-muted-foreground font-medium">Email:</span>
            <span className="ml-2">{user.email}</span>
          </div>
        </div>

        {/* Role */}
        <div className="flex items-center gap-3 p-2 rounded-2xl bg-muted/30">
          <Shield className="size-4 text-muted-foreground" />
          <div>
            <span className="text-muted-foreground font-medium">Role:</span>
            <span className="ml-2">{user.userType}</span>
          </div>
        </div>
        {/* Department */}
        <div className="flex items-center gap-3 p-2 rounded-2xl bg-muted/30">
          <Building className="size-4 text-muted-foreground" />
          <div className="flex-1">
            <span className="text-muted-foreground font-medium">
              Department:
            </span>
            {editField === "department" ? (
              <div className="flex gap-2 ml-2">
                <Select
                  size="small"
                  value={selected || ""}
                  onChange={(e) => setSelected(e.target.value)}
                >
                  {departments.map((d) => (
                    <MenuItem key={d.id} value={d.id}>
                      {d.name}
                    </MenuItem>
                  ))}
                </Select>
                <Button size="small" onClick={handleSave}>
                  Confirm
                </Button>
                <Button size="small" onClick={handleCancel} color="secondary">
                  Cancel
                </Button>
              </div>
            ) : (
              <>
                <span className="ml-2">{user.department}</span>
                <FontAwesomeIcon
                  FontAwesomeIcon
                  icon={"pencil"}
                  className="pl-2 fa fa-pencil cursor-pointer"
                  onClick={() => handleEdit("department")}
                />
              </>
            )}
          </div>
        </div>
        {/* Designation */}
        <div className="flex items-center gap-3 p-2 rounded-2xl bg-muted/30">
          <Briefcase className="size-4 text-muted-foreground" />
          <div className="flex-1">
            <span className="text-muted-foreground font-medium">
              Designation:
            </span>
            {editField === "designation" ? (
              <div className="flex gap-2 ml-2">
                <Select
                  size="small"
                  value={selected || ""}
                  onChange={(e) => setSelected(e.target.value)}
                >
                  {designations.map((d) => (
                    <MenuItem key={d.id} value={d.id}>
                      {d.name}
                    </MenuItem>
                  ))}
                </Select>
                <Button size="small" onClick={handleSave}>
                  Confirm
                </Button>
                <Button size="small" onClick={handleCancel} color="secondary">
                  Cancel
                </Button>
              </div>
            ) : (
              <>
                <span className="ml-2">{user.designation}</span>
                <FontAwesomeIcon
                  icon={"pencil"}
                  className="pl-2 fa fa-pencil cursor-pointer"
                  onClick={() => handleEdit("designation")}
                />
              </>
            )}
          </div>
        </div>
      </div>

      <hr />

      {/* actions */}
      {user?.status === "active" ? (
        <div className="grid grid-cols-2 gap-2 pt-2">
          <Link to={`/permission-manager/${user.userId}`}>
            <Button variant="contained" className="w-full">
              Manage Permission
            </Button>
          </Link>
          <Button
            variant="contained"
            onClick={() => setIsRoleModalOpen(true)}
            className="w-full"
          >
            Assign Role
          </Button>
        </div>
      ) : (
        <ActivateUser user={user} onClose={onClose} refetch={refetch} />
      )}

      {/* role modal */}
      <Modal isOpen={isRoleModalOpen} onClose={() => setIsRoleModalOpen(false)}>
        <AssignRole user={user} onClose={onClose} refetch={refetch} />
      </Modal>
    </div>
  );
};

export default UserActionModal;
